import React from 'react';
import styled from 'styled-components';
import { isFunction } from '@agilepoint/utils-common';
import { FlexBox, ActionButton, selectThemeSemantics, IBaseComponentProps, Typography } from '@agilepoint/wat-react-ux';
import { IFileInfo, IFileActions } from '../file-upload.types';
import { commonStyles } from 'common/components/style-literals';

const DeleteButton = styled(ActionButton)`
    :hover:enabled {
        .ap-icon {
            fill: ${selectThemeSemantics(({ palette }) => palette.red)};
        }
    }
`;

const FileLabel = styled(Typography.Medium)`
    background-color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.grayVariant5};
    padding: 5px 6px 4px 6px;
    margin: 6px;
    height: 28px;
    box-sizing: border-box;
    border-radius: ${selectThemeSemantics(({ borderRadius }) => borderRadius.curvedSmall)};
    ${commonStyles.textOverflow};
`;

const ItemWrapper = styled(FlexBox).attrs({
    className: String('ap-fileUpload-itemWrapper')
})`
    background-color: ${({ theme: { fileUpload: { colors: { item } } } }) => item.backgroundColor};
`;

interface IFilePreviewProps extends IBaseComponentProps<HTMLDivElement>, IFileInfo {
    onRemove(id: string): void;
    onRenderItemPreview?: (info: IFileInfo, actions: IFileActions) => React.ReactNode;
}

export const FilePreview = React.memo((props: IFilePreviewProps) => {
    const {
        name, elementRef, className, content, error, id, size, type,
        onRemove, onRenderItemPreview
    } = props;

    const _remove = React.useCallback(() => {
        onRemove(id);
    }, [id, onRemove]);

    const _onRemoveClick = React.useCallback((ev: React.MouseEvent) => {
        ev.stopPropagation();
        _remove();
    }, [_remove]);

    return (
        <ItemWrapper width="100%" className={className} ref={elementRef} justifyContent="space-between" alignItems="center">
            {
                isFunction(onRenderItemPreview)
                    ? onRenderItemPreview({ name, content, error, id, size, type }, { remove: _remove })
                    : (
                        <FlexBox justifyContent="space-between" width='100%' alignItems="center">
                            <FileLabel title={name}>{name}</FileLabel>
                            <DeleteButton onClick={_onRemoveClick} iconName="ap-close-round.solid" />
                        </FlexBox>
                    )
            }
        </ItemWrapper>
    );
});

FilePreview.displayName = 'FilePreview';
